/**
 * Inject the Google Analytics gtag.js snippet into <head>.
 * Idempotent — safe to call multiple times; will not double-inject.
 */
export function installGoogleAnalytics(measurementId) {
  if (!measurementId || typeof window === "undefined") return;
  if (window.__taxinearme_ga_installed) return;

  // Sanity check on the measurement ID format
  if (!/^(G-[A-Z0-9]{4,20}|UA-\d+-\d+)$/.test(measurementId)) {
    console.warn("Invalid Google Analytics ID format, skipping:", measurementId);
    return;
  }

  const script = document.createElement("script");
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(measurementId)}`;
  document.head.appendChild(script);

  const inline = document.createElement("script");
  inline.text = `
    window.dataLayer = window.dataLayer || [];
    function gtag(){dataLayer.push(arguments);}
    window.gtag = gtag;
    gtag('js', new Date());
    gtag('config', '${measurementId}', { anonymize_ip: true });
  `;
  document.head.appendChild(inline);

  window.__taxinearme_ga_installed = true;
}

/**
 * Inject the Google Ads gtag.js snippet (`AW-XXXXXXXXXX`). Idempotent.
 * If Google Analytics is already installed, the gtag runtime is reused —
 * we only add a fresh `gtag('config', 'AW-XXX')` line. Otherwise we bootstrap
 * gtag.js pointed at the Ads tag directly.
 */
export function installGoogleAds(conversionId) {
  if (!conversionId || typeof window === "undefined") return;
  if (!/^AW-\d{6,15}$/.test(conversionId)) {
    console.warn("Invalid Google Ads Conversion ID (expected AW-XXXXXXXXXX):", conversionId);
    return;
  }
  if (window.__taxinearme_ads_installed_ids && window.__taxinearme_ads_installed_ids.has(conversionId)) {
    return;
  }

  // Bootstrap gtag if GA hasn't already done it
  if (!window.__taxinearme_ga_installed) {
    const s = document.createElement("script");
    s.async = true;
    s.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(conversionId)}`;
    document.head.appendChild(s);
    const inline = document.createElement("script");
    inline.text = `
      window.dataLayer = window.dataLayer || [];
      function gtag(){dataLayer.push(arguments);}
      window.gtag = gtag;
      gtag('js', new Date());
    `;
    document.head.appendChild(inline);
  }

  const cfg = document.createElement("script");
  cfg.text = `gtag('config', '${conversionId}');`;
  document.head.appendChild(cfg);

  window.__taxinearme_ads_installed_ids = window.__taxinearme_ads_installed_ids || new Set();
  window.__taxinearme_ads_installed_ids.add(conversionId);
}

/**
 * Fire a purchase conversion event when a Stripe checkout completes.
 * - Google Ads: `event('conversion', { send_to: 'AW-.../label', value, currency, transaction_id })`
 * - GA4: `event('purchase', { transaction_id, value, currency, items })`
 * Both fire only if `gtag` is on the page (installed via consent).
 * Deduped in-memory + via `sessionStorage` by `transaction_id`.
 */
export function firePurchaseConversion({
  transactionId,
  value,
  currency = "EUR",
  adsConversionId,
  adsConversionLabel,
  routeName,
}) {
  if (typeof window === "undefined" || typeof window.gtag !== "function") return;
  if (!transactionId) return;
  const dedupeKey = `tnm_purchase_fired_${transactionId}`;
  try {
    if (sessionStorage.getItem(dedupeKey)) return;
    sessionStorage.setItem(dedupeKey, String(Date.now()));
  } catch (e) {
    console.warn("Purchase dedup sessionStorage unavailable (private mode?)", e);
  }
  window.__taxinearme_purchases_fired = window.__taxinearme_purchases_fired || new Set();
  if (window.__taxinearme_purchases_fired.has(transactionId)) return;
  window.__taxinearme_purchases_fired.add(transactionId);

  const numericValue = Number(value);
  const clean = Number.isFinite(numericValue) ? Number(numericValue.toFixed(2)) : 0;

  // 1) Google Ads conversion
  if (adsConversionId && adsConversionLabel) {
    window.gtag("event", "conversion", {
      send_to: `${adsConversionId}/${adsConversionLabel}`,
      value: clean,
      currency,
      transaction_id: transactionId,
    });
  }

  // 2) GA4 e-commerce purchase event (always fires when gtag is present).
  window.gtag("event", "purchase", {
    transaction_id: transactionId,
    value: clean,
    currency,
    items: routeName ? [{
      item_id: transactionId,
      item_name: routeName,
      price: clean,
      quantity: 1,
    }] : [],
  });
}

